/* eslint-disable react/no-unstable-nested-components */
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { Field } from "redux-form";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import FormCheckbox from "components/FormCheckbox";
import { useMaterialUIController } from "context";

export const CLIENT_RESERVATION_STATES = ["new", "update", "deleted"];

const CLIENT_RESERVATION_STATUS_LABEL_KEYS = {
  new: "emailClientReservationStatusNew",
  update: "emailClientReservationStatusUpdate",
  deleted: "emailClientReservationStatusDeleted",
};

const CLIENT_RESERVATION_TEXT_FIELDS = [
  { key: "subject", labelKey: "emailFieldSubjectSuffix" },
  { key: "emailTitle", labelKey: "emailFieldEmailTitle" },
  { key: "emailText", labelKey: "emailFieldEmailText" },
];

const SUPPORTED_LOCALES = ["es", "en"];

const CLIENT_RESERVATION_DEFAULT_TEXTS = {
  new: {
    es: {
      subject: "Reserva de apartamento confirmada",
      emailTitle: "Reserva de apartamento confirmada",
      emailText: "Tu reserva de apartamento se ha creado correctamente. Estos son los detalles:",
    },
    en: {
      subject: "Apartment reservation confirmed",
      emailTitle: "Apartment reservation confirmed",
      emailText: "Your apartment reservation has been created successfully. Here are the details:",
    },
  },
  update: {
    es: {
      subject: "Reserva de apartamento actualizada",
      emailTitle: "Reserva de apartamento actualizada",
      emailText: "Tu reserva de apartamento ha sido actualizada. Estos son los nuevos detalles:",
    },
    en: {
      subject: "Apartment reservation updated",
      emailTitle: "Apartment reservation updated",
      emailText: "Your apartment reservation has been updated. Here are the new details:",
    },
  },
  deleted: {
    es: {
      subject: "Reserva de apartamento cancelada",
      emailTitle: "Reserva de apartamento cancelada",
      emailText: "Tu reserva de apartamento ha sido cancelada. Estos son los detalles:",
    },
    en: {
      subject: "Apartment reservation canceled",
      emailTitle: "Apartment reservation canceled",
      emailText: "Your apartment reservation has been canceled. Here are the details:",
    },
  },
};

const getTextValue = (statusConfig, fieldKey, locale) => {
  const value = statusConfig?.texts?.[fieldKey]?.[locale];
  return typeof value === "string" ? value : "";
};

const getDefaultTextPlaceholder = (statusKey, fieldKey, locale) => {
  const stateKey = CLIENT_RESERVATION_DEFAULT_TEXTS[statusKey] ? statusKey : "new";
  const safeLocale = locale === "en" ? "en" : "es";
  return CLIENT_RESERVATION_DEFAULT_TEXTS[stateKey]?.[safeLocale]?.[fieldKey] || "";
};

const buildTextsByFieldAndLocale = (statusConfig = {}) =>
  Object.fromEntries(
    CLIENT_RESERVATION_TEXT_FIELDS.map((field) => [
      field.key,
      Object.fromEntries(
        SUPPORTED_LOCALES.map((locale) => [locale, getTextValue(statusConfig, field.key, locale)])
      ),
    ])
  );

export const buildClientReservationStatesPayload = (statesSource) =>
  Object.fromEntries(
    CLIENT_RESERVATION_STATES.map((statusKey) => {
      const statusConfig = statesSource?.[statusKey] || {};

      return [
        statusKey,
        {
          enabled: Boolean(statusConfig?.enabled),
          texts: buildTextsByFieldAndLocale(statusConfig),
          accessMethods: statusConfig?.accessMethods || { web: false, app: false },
        },
      ];
    })
  );

export const buildClientReservationExpandedStates = () =>
  Object.fromEntries(CLIENT_RESERVATION_STATES.map((stateKey) => [stateKey, true]));

function ClientReservationTextFields({ statusKey, t }) {
  return (
    <Grid container spacing={2} sx={{ marginLeft: 5 }}>
      {statusKey === "new" || statusKey === "update" ? (
        <Grid size={{ xs: 12 }} key={`${statusKey}-access-methods`}>
          <MDTypography
            variant="button"
            fontWeight="medium"
            mb={1}
            display="block"
            sx={{ textTransform: "uppercase", fontSize: 14, marginTop: 3 }}
          >
            {t("emailAccessMethodsSection")}
          </MDTypography>
          <Grid container spacing={2}>
            <Field
              name={`emailTemplates.clientReservation.states.${statusKey}.accessMethods.web`}
              type="checkbox"
              component={FormCheckbox}
              label={t("emailAccessMethodWeb")}
            />
            <Field
              name={`emailTemplates.clientReservation.states.${statusKey}.accessMethods.app`}
              type="checkbox"
              component={FormCheckbox}
              label={t("emailAccessMethodApp")}
            />
          </Grid>
        </Grid>
      ) : null}

      {CLIENT_RESERVATION_TEXT_FIELDS.map((field) => (
        <Grid size={{ xs: 12 }} key={`${statusKey}-${field.key}`}>
          <MDTypography
            variant="button"
            fontWeight="medium"
            mb={1}
            display="block"
            sx={{ textTransform: "uppercase", fontSize: 14, marginTop: 3 }}
          >
            {t(field.labelKey)}
          </MDTypography>
          <Grid container spacing={2}>
            {SUPPORTED_LOCALES.map((locale) => (
              <Grid size={{ xs: 12, md: 6 }} key={locale}>
                <Field
                  name={`emailTemplates.clientReservation.states.${statusKey}.texts.${field.key}.${locale}`}
                  component={FormField}
                  label={`${t(field.labelKey)} (${locale.toUpperCase()})`}
                  placeholder={getDefaultTextPlaceholder(statusKey, field.key, locale)}
                  multiline
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

ClientReservationTextFields.propTypes = {
  statusKey: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

function ClientReservationStatusSection({ statusKey, isExpanded, onToggleExpand, t }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <Grid size={{ xs: 12 }} key={statusKey}>
      <MDBox
        p={2}
        borderRadius="lg"
        style={{
          border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
          background: darkMode ? "#26304b" : "#f3f4f6",
        }}
      >
        <Field
          name={`emailTemplates.clientReservation.states.${statusKey}.enabled`}
          component={({ input: statusInput }) => (
            <>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Checkbox
                  checked={!!statusInput.value}
                  onChange={(event) => statusInput.onChange(event.target.checked)}
                />
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: 16,
                    textTransform: "uppercase",
                    color: darkMode ? "#f8fafc" : "#0f172a",
                  }}
                  onClick={() => onToggleExpand(statusKey)}
                >
                  {t(CLIENT_RESERVATION_STATUS_LABEL_KEYS[statusKey])}
                </MDTypography>
              </MDBox>

              {statusInput.value && isExpanded && (
                <ClientReservationTextFields statusKey={statusKey} t={t} />
              )}
            </>
          )}
        />
      </MDBox>
    </Grid>
  );
}

ClientReservationStatusSection.propTypes = {
  statusKey: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function ClientReservationEmailSection({
  expandedStates,
  isTemplateExpanded,
  onToggleStateExpanded,
  onToggleTemplateExpanded,
  t,
  templateInput,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Checkbox
            checked={!!templateInput.value}
            onChange={(event) => templateInput.onChange(event.target.checked)}
          />
          <MDTypography
            variant="button"
            fontWeight="medium"
            textTransform="uppercase"
            sx={{
              cursor: "pointer",
              userSelect: "none",
              fontSize: 18,
              color: darkMode ? "#f8fafc" : "#0f172a",
            }}
            onClick={onToggleTemplateExpanded}
          >
            {t("emailClientReservation")}
          </MDTypography>
        </MDBox>
      </Grid>

      {templateInput.value &&
        isTemplateExpanded &&
        CLIENT_RESERVATION_STATES.map((statusKey) => (
          <ClientReservationStatusSection
            key={statusKey}
            statusKey={statusKey}
            isExpanded={!!expandedStates[statusKey]}
            onToggleExpand={onToggleStateExpanded}
            t={t}
          />
        ))}
    </>
  );
}

ClientReservationEmailSection.propTypes = {
  expandedStates: PropTypes.object.isRequired,
  isTemplateExpanded: PropTypes.bool.isRequired,
  onToggleStateExpanded: PropTypes.func.isRequired,
  onToggleTemplateExpanded: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  templateInput: PropTypes.shape({
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
};

export default ClientReservationEmailSection;
